<template>
  <article
    v-if="product"
    ref="productCard"
    class="product prod-card prod-card--linked"
    :class="[
      { 'is-loading': states.component !== 'ready' },
      { 'is-hoverable': secondaryImage },
    ]"
  >
    <figure class="prod-card__media-container">
      <a
        class="prod-card__block-link"
        :href="product.url"
        :aria-label="product.title"
        @click="handleLinkClick"
      >
        <div class="prod-card__media-carousel">
          <ul ref="mediaCarouselContainer" class="prod-card__media">
            <li
              :id="`${product.id}-media-main`"
              ref="carouselMediaMain"
              class="prod-card__media-item"
              :class="{ 'prod-card__media-item-with-hover': secondaryImage }"
            >
              <responsive-image
                :image="primaryImage"
                min-max="180-620"
                :sizes="imageSizes"
              />
              <responsive-image
                v-if="secondaryImage"
                :image="secondaryImage"
                min-max="180-620"
                :sizes="imageSizes"
              />
            </li>
          </ul>
        </div>
      </a>
    </figure>

    <header class="prod-card__header">
      <div class="prod-card__details">
        <div class="prod-card__details-top">
          <a
            class="prod-card__link"
            :href="product.url"
            @click="handleLinkClick"
          >
            <h3 class="prod-card__title" v-text="product.title" />
          </a>
        </div>

        <product-prices
          class="prod-card__prices"
          :currency-code="currencyCode"
          :product="product"
        />
      </div>
    </header>
  </article>
</template>

<script>
  /**
   * Component: Product Card Link
   * ------------------------------------------------------------------------------
   * - Simplified Product Card component that acts as a link to the product's page
   *   without any additional features.
   * - Styles are loaded in via theme.scss
   * @param {String} [context] - Location of the Product Card. Enables variating functionality per-location. For example: context: `collection`
   * @param {String} [initialProduct] - Formatted Shopify product.
   * @param {Boolean} [isPlpProduct] - Indicates whether product card is being rendered on the PLP.
   * @param {Object} [trackingParameters] - Object of parameters, typically used for GTM, Bloomreach etc. tracking.
   *
   */
  import { mapActions, mapState } from 'pinia'

  import { useCartStore } from '~stores/cart'
  import { useLocaleStore } from '~stores/locale'
  import { useScreenStore } from '~stores/screen'

  import breakpoints from '~/helpers/breakpoints'

  import ProductPrices from '~components/product-prices/product-prices'
  import ResponsiveImage from '~components/responsive-image/responsive-image'

  export default {
    name: 'ProductCardLink',

    components: {
      ResponsiveImage,
      ProductPrices,
    },

    props: {
      context: {
        type: String,
        default: 'default',
      },
      initialProduct: {
        type: [Boolean, Object],
        default: false,
      },
      isPlpProduct: {
        type: Boolean,
        default: false,
      },
      trackingParameters: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['link-clicked'],

    data() {
      return {
        currentVariant: false,
        mediaCarousel: {
          firstSlideVisible: true,
          lastSlideVisible: false,
        },
        product: {
          handle: '',
          id: '',
          images: [],
          price: {},
          title: '',
          variants: [],
        },
        states: {
          component: 'loading',
          currency: 'loading',
        },
      }
    },

    computed: {
      /**
       * Map locale state.
       */
      ...mapState(useLocaleStore, {
        currencyCode: 'currencyCode',
      }),

      /**
       * Map viewport state.
       */
      ...mapState(useScreenStore, {
        isMobile: 'mobile',
      }),

      /**
       * Returns an accessible label for the carousels 'Next' button.
       * @returns {String}
       */
      carouselNextLabel() {
        return mint.strings.accessibility.carousel.next
      },

      /**
       * Returns an accessible label for the carousels 'Prev' button.
       * @returns {String}
       */
      carouselPrevLabel() {
        return mint.strings.accessibility.carousel.prev
      },

      /**
       * Calculate if to disable the 'Previous' button.
       * @returns {Boolean}
       */
      disableNextCarouselButton() {
        if (this.states.component !== 'ready') {
          return true
        }

        return this.mediaCarousel.lastSlideVisible
      },

      /**
       * Calculate if to disable the 'Previous' button.
       * @returns {Boolean}
       */
      disablePrevCarouselButton() {
        if (this.states.component !== 'ready') {
          return true
        }

        return this.mediaCarousel.firstSlideVisible
      },

      /**
       * Calculate responsive srcset `sizes` attribute for the images.
       * @returns {String}
       */
      imageSizes() {
        return `
        100vw,
        (min-width: ${breakpoints.large}) 50vw
      `
      },

      /**
       * Calculate the primary image to display.
       * @returns {Boolean|Object}
       */
      primaryImage() {
        if (!this.product || !this.product.featuredImage) {
          return false
        }

        return this.product.featuredImage
      },

      /**
       * Calculate the secondary image to display.
       * - Do not show gallery images on PLPs
       * - Do not show if only one image.
       * - Do not show second image on mobile devices.
       * @returns {Boolean|Object}
       */
      secondaryImage() {
        if (!mint.flags.productCards.enableHoverImage) {
          return false
        }

        return !this.product?.images ||
          !this.product.images.length > 1 ||
          this.isMobile
          ? false
          : this.product.images[1]
      },
    },

    watch: {
      /**
       * Watch for the product object to set.
       * - Runs once on first change, once shallow objects are populated.
       */
      product() {
        if (this.states.component === 'ready' || !this.product?.handle) {
          return
        }

        this.currentVariant = this.product.variants[0]

        this.setProductAttributes()

        this.states.component = this.product?.handle ? 'ready' : 'loading'
      },
    },

    created() {
      if (this.initialProduct) {
        this.product = this.getInitialProductObject()
      }
    },

    mounted() {},

    methods: {
      /**
       * Map Cart Actions.
       */
      ...mapActions(useCartStore, ['addItems']),

      /**
       * Set custom attributes for the product.
       * - These are mostly used for event tracking.
       * - Products from the PLP will also have an 'attraqt' object.
       */
      setProductAttributes() {
        const attributes = this.product.attributes
        const source = this.trackingParameters

        this.product.attributes = {
          ...attributes,
          source,
        }
      },

      /**
       * If a product object is passed in, return that to be assigned to
       * this.product on `created`.
       * - Attraqt will return an image URL, not the image object (like Shopify).
       * - If the product source is Attraqt, then manually add the aspect
       * ratio of a product card to the image objects. This allows the image
       * component to correctly render without CLS.
       * @returns {Object}
       */
      getInitialProductObject() {
        if (this.product.source !== 'attraqt') {
          return this.initialProduct
        }

        const initialProduct = this.initialProduct

        initialProduct.images = initialProduct.images.map((image) => {
          return {
            ...image,
            aspectRatio: '0.728',
          }
        })

        initialProduct.featuredImage = initialProduct.images[0]

        return initialProduct
      },

      /**
       * Handle events for clicking the link.
       * - Used to trigger 'Product Card Click' interaction events.
       * - IMPORTANT: Given the whole product card is a clickable link and also
       *   includes child elements with their own click events (Wishlist, Variant Selector)
       *   those click events need to have `@click.stop.prevent` to prevent
       *   the below from triggering unintentionally.
       */
      handleLinkClick() {
        // if (!mint.customer.canBeTracked) {
        //   return
        // }

        // const attributes = JSON.parse(JSON.stringify(this.product.attributes))
        // window.console.log('Track any Product Card Links here...', attributes)

        /**
         * Send click event to the parent component.
         * - Can be used for tracking events and such.
         * - Also used to preserve user scroll position on PLP 'Back' button.
         */
        this.$emit('link-clicked', {
          handle: this.product.handle,
          product: this.product,
        })

        if (this.trackingParameters.source === 'Bloomreach Recommendations') {
          const trackedEvent = {
            event: 'recommendation',
            data: {
              action: 'click',
              recommendation_id: this.trackingParameters.recommendation_id,
              item_id: Number(this.product.id),
            },
          }

          mint.EventBus.dispatch('bloomreach:trackEvent', trackedEvent)
        }
      },

      /**
       * Overrides Aspect Ratio back to auto for Trending Products
       * @param {String} aspectRatio - Valid CSS aspect ratio
       */
      setAspectRatio(aspectRatio) {
        this.$refs.productCard.style.setProperty('--aspect-ratio', aspectRatio)
      },
    },
  }
</script>
